import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_TONES,
  CampaignCard,
  CampaignCardAction,
  CampaignCardBody,
  CampaignCardHeader,
} from '#/entities/campaign'
import type { CampaignSequenceSwitch } from '#/entities/campaign'
import { cn } from '#/shared/lib/utils'
import { Toggle } from '#/shared/ui/toggle'

import type { CampaignSequenceRail as CampaignSequenceRailModel } from '../model/use-campaign-sequence'

interface SwitchListProps {
  rows: CampaignSequenceSwitch[]
  onToggle: (key: string, enabled: boolean) => void
  busy: boolean
}

/**
 * A list of switches whose state is the service's. Locked rows are
 * compliance, not preference — they render disabled and read in the muted ink
 * so nobody hunts for a way to turn them off.
 */
function SwitchList({ rows, onToggle, busy }: SwitchListProps) {
  return (
    <>
      {rows.map((row) => (
        <div key={row.key} className="flex items-start gap-2.5">
          <Toggle
            checked={row.enabled}
            disabled={row.locked === true || busy}
            aria-label={row.label}
            onCheckedChange={(next) => onToggle(row.key, next)}
          />
          <div className="flex min-w-0 flex-col gap-0.75">
            <p
              className={cn(
                'text-xs font-medium',
                row.locked ? 'text-gray-500' : 'text-gray-900',
              )}
            >
              {row.label}
            </p>
            <p className="text-xs text-gray-300">{row.note}</p>
          </div>
        </div>
      ))}
    </>
  )
}

interface CampaignSequenceRailProps {
  rail: CampaignSequenceRailModel
  onToggle: (key: string, enabled: boolean) => void
  busy: boolean
}

export function CampaignSequenceRail({
  rail,
  onToggle,
  busy,
}: CampaignSequenceRailProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const reply = rail.replyHandling
  return (
    <div className="flex flex-col gap-4">
      <CampaignCard>
        <CampaignCardHeader
          dense
          label={t('campaigns.sequence.sections.exitRules')}
        />
        <CampaignCardBody dense className="gap-3">
          <SwitchList rows={rail.exitRules} onToggle={onToggle} busy={busy} />
          <p className="text-xs text-gray-300">{rail.exitNote}</p>
        </CampaignCardBody>
      </CampaignCard>

      {reply != null && (
        <CampaignCard>
          {/* No divider under this one in the design — the headline carries it. */}
          <div className="flex items-center justify-between gap-2 px-3.5 py-3">
            <h2 className="text-2xs font-medium tracking-wider text-gray-300">
              {t('campaigns.sequence.sections.replyHandling')}
            </h2>
            {/* Email replies land in the shared inbox; SMS replies land in
                Phone, which is a panel rather than a page. The link goes to
                the one that is a destination. */}
            <CampaignCardAction onClick={() => void navigate({ to: '/inbox' })}>
              {t('campaigns.sequence.actions.openInbox')}
            </CampaignCardAction>
          </div>
          <div className="flex flex-col gap-1.5 px-3.5 pb-3.5">
            <p className="text-base font-semibold text-campaign-attention">
              {reply.headline}
            </p>
            <p className="text-xs text-gray-300">{reply.note}</p>
            <p className="text-xs text-warm-500">{reply.stats}</p>
          </div>
        </CampaignCard>
      )}

      <CampaignCard>
        <CampaignCardHeader
          dense
          label={t('campaigns.sequence.sections.coverage')}
        />
        <CampaignCardBody dense className="gap-3">
          {rail.coverage.map((row) => (
            <div
              key={row.key}
              className="flex items-center justify-between gap-3"
            >
              <span className="text-xs text-gray-500">{row.label}</span>
              <span
                className={cn(
                  'text-xs font-medium',
                  CAMPAIGN_TONES[row.tone ?? 'default'],
                )}
              >
                {row.value}
              </span>
            </div>
          ))}
          <p className="text-xs text-gray-300">{rail.coverageNote}</p>
        </CampaignCardBody>
      </CampaignCard>

      <CampaignCard>
        <CampaignCardHeader
          dense
          label={t('campaigns.sequence.sections.sendingWindow')}
        />
        <CampaignCardBody dense className="gap-3">
          <SwitchList rows={rail.window} onToggle={onToggle} busy={busy} />
        </CampaignCardBody>
      </CampaignCard>
    </div>
  )
}
